import React, { useEffect, useState } from "react"
import { Grid, Box } from "@mui/material"
import { IconSeeker } from "@ui-seeker/core"
import PageWrapper from "Pages/@PageWrapper"
import { AppCardTitle, AppCrudTable, AppButtonIcon, AppCheckCrossIcon, AppFilterChip, AppFilterPanel, AppPillToggle, AppResultsSkeleton } from "@components"
import { AUTO_EUROPE_TOOL_ROUTES } from "@constants/routes"
import {
	autoEuropeCheckersTableData,
	autoEuropeCheckersTableIsLoading,
} from "Pages/AutoEuropeTool/Checkers/@signals"
import useAutoEuropeCheckersTable from "Pages/AutoEuropeTool/Checkers/@hooks/useTable"
import GroupedCheckers from "Pages/AutoEuropeTool/Checkers/@components/GroupedCheckers"
import { dsColorNew } from "@palette"
import WarningsSection from "Pages/AutoEuropeTool/Checkers/@components/WarningsSection"

// Filtros rapidos acumulables: se aplican en AND en las vistas Table y Warnings.
const QUICK_FILTERS = [
	{ key: 'notChecked', label: 'Not checked', test: row => !row.checked },
	{ key: 'notExported', label: 'Not exported', test: row => !row.exported },
	{ key: 'noNotifications', label: 'Notifications = 0', test: row => !row.numNotifications },
	{ key: 'withWarning', label: 'With warning', test: row => !!row.warning },
]

// En Warnings todas las filas tienen warning, asi que ese filtro no discrimina.
const WARNING_FILTERS = QUICK_FILTERS.filter(f => f.key !== 'withWarning')

// Warnings arranca acotado a las ejecuciones sin notificaciones, que son las que
// hay que mirar primero; el chip queda activo y se puede quitar.
const DEFAULT_WARNING_FILTERS = ['noNotifications']

const centered = v => <Box sx={{ display: 'flex', justifyContent: 'center' }}>{v ?? ''}</Box>

const textFilterLogic = (value, filters) => {
	if (!filters?.[0]) return false
	return !String(value ?? '').toLowerCase().includes(String(filters[0]).toLowerCase())
}

const textFilter = { filterType: 'textField', filterOptions: { logic: textFilterLogic } }

const buildColumns = onOpenNotifications => [
	{ name: 'toolName', label: 'Pricing Tool', options: { ...textFilter, customBodyRowRender: v => centered(v) } },
	{ name: 'reportName', label: 'Report', options: { ...textFilter, customBodyRowRender: v => centered(v) } },
	{ name: 'executionTime', label: 'Execution', options: { filter: false, customBodyRowRender: v => centered(v) } },
	{ name: 'idOfficePickupName', label: 'Office', options: { ...textFilter, customBodyRowRender: v => centered(v) } },
	{
		name: 'checked',
		label: 'Checked',
		options: {
			// When checked, the icon links to the Notifications page with the row selection prefilled.
			customBodyRowRender: (v, row) => centered(v
				? <AppButtonIcon
					icon="check"
					tooltip="View notifications"
					sx={{ color: dsColorNew.successText }}
					onClick={() => onOpenNotifications(row)}
				/>
				: <AppCheckCrossIcon condition={false}/>),
		},
	},
	{ name: 'numNotifications', label: 'Num Notifications', options: { filter: false, customBodyRowRender: v => centered(v ?? 0) } },
	{
		name: 'exported',
		label: 'Exported',
		// When exported and an export URL is available, show a download link instead of the check icon.
		options: {
			customBodyRowRender: (v, row) => centered(v && row.exportUrl
				? <AppButtonIcon
					icon="file-arrow-down"
					tooltip="Download export"
					sx={{ color: dsColorNew.appBlue }}
					onClick={() => window.open(row.exportUrl, '_blank')}
				/>
				: <AppCheckCrossIcon condition={!!v}/>),
		},
	},
	{
		name: 'warning',
		label: 'Warning',
		// El detalle esta en la seccion de Warnings; aqui solo el aviso con el motivo al pasar.
		options: {
			customBodyRowRender: (v, row) => centered(v
				? <IconSeeker
					icon="triangle-exclamation"
					tooltip={(row.warningReasons ?? []).join(', ')}
					sx={{ color: dsColorNew.noteAmber }}
				/>
				: ''),
		},
	},
]

export default function AutoEuropeCheckersPage() {
	const { getTableData } = useAutoEuropeCheckersTable()
	const [view, setView] = useState('grouped')

	// Estado de filtros por vista: Warnings parte preseleccionada y no queremos que
	// eso arrastre a Table, ni que lo que se marque en Table acote los warnings.
	const [tableFilters, setTableFilters] = useState([])
	const [warningFilters, setWarningFilters] = useState(DEFAULT_WARNING_FILTERS)

	useEffect(() => {
		getTableData()
	}, [])

	// Los filtros rapidos son de Table y Warnings: en Grouped el arbol ya agrupa y
	// filtrar por debajo dejaria nodos vacios sin explicacion.
	const isTable = view === 'table'
	const isWarnings = view === 'warnings'
	const quickFilters = isWarnings ? WARNING_FILTERS : QUICK_FILTERS
	const activeFilters = isWarnings ? warningFilters : tableFilters
	const setFilters = isWarnings ? setWarningFilters : setTableFilters

	const toggleFilter = key => setFilters(prev =>
		prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key])

	const allRows = autoEuropeCheckersTableData.value

	const applyFilters = list => activeFilters.length
		? list.filter(row => quickFilters.every(f => !activeFilters.includes(f.key) || f.test(row)))
		: list

	const rows = isTable ? applyFilters(allRows) : allRows

	// Cada vista cuenta sobre su propio universo: en Warnings los contadores de los
	// chips solo tienen sentido sobre las ejecuciones con warning.
	const allWarningRows = allRows.filter(row => row.warning)
	const warningRows = isWarnings ? applyFilters(allWarningRows) : allWarningRows
	const filterBaseRows = isWarnings ? allWarningRows : allRows

	const onOpenNotifications = row => {
		const params = new URLSearchParams({
			toolId: row.toolId ?? '',
			idReport: row.idReport ?? '',
			idReportExecution: row.idReportExecution ?? '',
			idOfficePickup: row.idOfficePickup ?? '',
		})
		window.open(`${AUTO_EUROPE_TOOL_ROUTES.NOTIFICATIONS_PATH}?${params.toString()}`, '_blank')
	}

	return <PageWrapper
		head={`DS Rentacar | ${AUTO_EUROPE_TOOL_ROUTES.SUBTITLE}`}
		title={AUTO_EUROPE_TOOL_ROUTES.CHECKERS_TITLE}
		subtitle={AUTO_EUROPE_TOOL_ROUTES.CHECKERS_SUBTITLE}
	>
		<AppFilterPanel toggles={<>
			<AppPillToggle
				size="small"
				value={view}
				onChange={setView}
				options={[
					{ value: 'grouped', label: <><IconSeeker icon="sitemap" sx={{ mr: 1, fontSize: 12 }}/>Grouped</> },
					{ value: 'table', label: <><IconSeeker icon="table" sx={{ mr: 1, fontSize: 12 }}/>Table</> },
					{ value: 'warnings', label: <>
						<IconSeeker icon="triangle-exclamation" sx={{ mr: 1, fontSize: 12, ...(allWarningRows.length && { color: dsColorNew.noteAmber }) }}/>
						Warnings{allWarningRows.length ? ` (${allWarningRows.length})` : ''}
					</> },
				]}
			/>
			{(isTable || isWarnings) && <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
				{quickFilters.map(f => <AppFilterChip
					key={f.key}
					label={`${f.label} (${filterBaseRows.filter(f.test).length})`}
					selected={activeFilters.includes(f.key)}
					onClick={() => toggleFilter(f.key)}
				/>)}
			</Box>}
		</>}/>
		<Grid container spacing={2}>
			<Grid item xs={12}>
				{autoEuropeCheckersTableIsLoading.value
					? <AppResultsSkeleton rows={7} columns={6}/>
					: <>
						{view === 'warnings' && <WarningsSection rows={warningRows} isFiltered={!!warningFilters.length}/>}
						{view === 'grouped' && <GroupedCheckers
							data={rows}
							onOpenNotifications={onOpenNotifications}
						/>}
						{view === 'table' && <AppCrudTable
							title={<AppCardTitle icon="list-check" title="Checkers"/>}
							data={rows}
							columns={buildColumns(onOpenNotifications)}
						/>}
					</>
				}
			</Grid>
		</Grid>
	</PageWrapper>
}